import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { authClient } from '@/lib/auth-client';
import { can, isStaffFromPermissions } from './staff';

export function useCurrentUser() {
  const { data: session } = authClient.useSession();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/api/v1/me'),
    enabled: Boolean(session),
    retry: false,
  });
}

export function useCan(permission: string): boolean {
  const { data } = useCurrentUser();
  return can(data?.permissions, permission);
}

export function useIsStaff(): boolean {
  const { data } = useCurrentUser();
  return isStaffFromPermissions(data?.permissions);
}

/** True when the account has the admin wildcard (or the admin role during load). */
export function useIsAdmin(): boolean {
  const { data } = useCurrentUser();
  return can(data?.permissions, '*') || Boolean(data?.roles.includes('admin'));
}

/**
 * May create and edit content: the permission plus the admin's "content editor" switch for this
 * teacher. The server enforces both; this only hides buttons that would be refused.
 */
export function useCanEditContent(): boolean {
  const { data } = useCurrentUser();
  return can(data?.permissions, 'content.write') && (data?.staff?.contentEditor ?? true);
}
